// ===================================
// ADMIN CATEGORIES PAGE - FIXED
// src/pages/AdminCategories.js
// FIX: Use api service instead of raw fetch
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './AdminCategories.css';

const AdminCategories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);

  // Category form state
  const [categoryForm, setCategoryForm] = useState({
    slug: '',
    name: '',
    description: '',
    icon: '📋',
    requires_resume: false,
    requires_job_description: false,
    requires_company_name: false,
    display_order: 0
  });

  // Subcategory form state
  const [subcategoryForm, setSubcategoryForm] = useState({
    slug: '',
    name: '',
    description: '',
    icon: '📌',
    display_order: 0
  });

  // FIXED: Use api service instead of raw fetch
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/categories/admin/all');
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubcategories = useCallback(async (categoryId) => {
    if (!categoryId) return;
    
    try {
      const category = categories.find(c => c.id === categoryId);
      if (!category) return;

      const response = await api.get(`/categories/${category.slug}/subcategories`);
      setSubcategories(response.data.subcategories || []);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setSubcategories([]);
    }
  }, [categories]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories(selectedCategory);
    }
  }, [selectedCategory, fetchSubcategories]);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await api.put(`/categories/admin/${editingCategory.id}`, categoryForm);
      } else {
        await api.post('/categories/admin', categoryForm);
      }
      
      setShowCategoryModal(false);
      setEditingCategory(null);
      resetCategoryForm();
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to save category');
    }
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSubcategory) {
        await api.put(`/categories/admin/subcategories/${editingSubcategory.id}`, subcategoryForm);
      } else {
        await api.post(`/categories/admin/${selectedCategory}/subcategories`, subcategoryForm);
      }
      
      setShowSubcategoryModal(false);
      setEditingSubcategory(null);
      resetSubcategoryForm();
      fetchSubcategories(selectedCategory);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to save subcategory');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to deactivate this category?')) return;
    
    try {
      await api.delete(`/categories/admin/${categoryId}`);
      fetchCategories();
    } catch (err) {
      alert('Failed to delete category');
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    if (!window.confirm('Are you sure you want to deactivate this subcategory?')) return;
    
    try {
      await api.delete(`/categories/admin/subcategories/${subcategoryId}`);
      fetchSubcategories(selectedCategory);
    } catch (err) {
      alert('Failed to delete subcategory');
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      slug: category.slug,
      name: category.name,
      description: category.description || '',
      icon: category.icon || '📋',
      requires_resume: category.requires_resume,
      requires_job_description: category.requires_job_description,
      requires_company_name: category.requires_company_name,
      display_order: category.display_order
    });
    setShowCategoryModal(true);
  };

  const openEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      slug: subcategory.slug,
      name: subcategory.name,
      description: subcategory.description || '',
      icon: subcategory.icon || '📌',
      display_order: subcategory.display_order
    });
    setShowSubcategoryModal(true);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      slug: '',
      name: '',
      description: '',
      icon: '📋',
      requires_resume: false,
      requires_job_description: false,
      requires_company_name: false,
      display_order: 0
    });
  };

  const resetSubcategoryForm = () => {
    setSubcategoryForm({
      slug: '',
      name: '',
      description: '',
      icon: '📌',
      display_order: 0
    });
  };

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);

  if (loading) {
    return (
      <div className="admin-categories-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-categories-page">
      <div className="page-header">
        <div className="header-content">
          <h1>📋 Interview Categories</h1>
          <p>Manage interview categories and subcategories</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            resetCategoryForm();
            setEditingCategory(null);
            setShowCategoryModal(true);
          }}
        >
          + Add Category
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={fetchCategories} className="btn btn-sm btn-secondary" style={{ marginLeft: '1rem' }}>
            Retry
          </button>
        </div>
      )}

      <div className="categories-layout">
        {/* Categories List */}
        <div className="categories-panel">
          <h2>Categories</h2>
          <div className="categories-list">
            {categories.length === 0 ? (
              <div className="empty-state">
                <p>No categories found</p>
              </div>
            ) : (
              categories.map(category => (
                <div 
                  key={category.id}
                  className={`category-item ${selectedCategory === category.id ? 'selected' : ''} ${!category.is_active ? 'inactive' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <div className="category-icon">{category.icon || '📋'}</div>
                  <div className="category-info">
                    <h3>{category.name}</h3>
                    <p>{category.description}</p>
                    <div className="category-meta">
                      <span className="badge">{category.subcategory_count || 0} subcategories</span>
                      <span className="badge">{category.session_count || 0} sessions</span>
                      {!category.is_active && <span className="badge badge-warning">Inactive</span>}
                    </div>
                  </div>
                  <div className="category-actions">
                    <button 
                      className="btn btn-icon"
                      onClick={(e) => { e.stopPropagation(); openEditCategory(category); }}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn btn-icon btn-danger"
                      onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Subcategories Panel */}
        <div className="subcategories-panel">
          {selectedCategoryData ? (
            <>
              <div className="subcategories-header">
                <h2>{selectedCategoryData.icon} {selectedCategoryData.name} - Subcategories</h2>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    resetSubcategoryForm();
                    setEditingSubcategory(null);
                    setShowSubcategoryModal(true);
                  }}
                >
                  + Add Subcategory
                </button>
              </div>
              
              <div className="category-settings">
                <h4>Category Settings</h4>
                <div className="settings-badges">
                  {selectedCategoryData.requires_resume && <span className="badge">📄 Resume Required</span>}
                  {selectedCategoryData.requires_job_description && <span className="badge">📝 JD Required</span>}
                  {selectedCategoryData.requires_company_name && <span className="badge">🏢 Company Required</span>}
                </div>
              </div>

              <div className="subcategories-list">
                {subcategories.length === 0 ? (
                  <div className="empty-state">
                    <p>No subcategories for this category</p>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        resetSubcategoryForm();
                        setEditingSubcategory(null);
                        setShowSubcategoryModal(true);
                      }}
                    >
                      Create First Subcategory
                    </button>
                  </div>
                ) : (
                  subcategories.map(sub => (
                    <div key={sub.id} className={`subcategory-item ${!sub.is_active ? 'inactive' : ''}`}>
                      <div className="subcategory-icon">{sub.icon || '📌'}</div>
                      <div className="subcategory-info">
                        <h4>{sub.name}</h4>
                        <p>{sub.description}</p>
                      </div>
                      <div className="subcategory-actions">
                        <button 
                          className="btn btn-icon"
                          onClick={() => openEditSubcategory(sub)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn btn-icon btn-danger"
                          onClick={() => handleDeleteSubcategory(sub.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <span className="icon">👈</span>
              <p>Select a category to view its subcategories</p>
            </div>
          )}
        </div>
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'New Category'}</h2>
              <button className="modal-close" onClick={() => setShowCategoryModal(false)}>×</button>
            </div>
            <form onSubmit={handleCategorySubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Icon</label>
                    <input
                      type="text"
                      value={categoryForm.icon}
                      onChange={e => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                      className="form-input icon-input"
                      placeholder="📋"
                    />
                  </div>
                  <div className="form-group flex-grow">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      className="form-input"
                      required
                      placeholder="e.g., HR Interview"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    value={categoryForm.slug}
                    onChange={e => setCategoryForm({ ...categoryForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="form-input"
                    required
                    placeholder="e.g., hr-interview"
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    className="form-input"
                    rows={3}
                    placeholder="Brief description of this category"
                  />
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={categoryForm.display_order}
                    onChange={e => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })}
                    className="form-input"
                    min={0}
                  />
                </div>

                <div className="form-group">
                  <label>Requirements</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={categoryForm.requires_resume}
                        onChange={e => setCategoryForm({ ...categoryForm, requires_resume: e.target.checked })}
                      />
                      Requires Resume
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={categoryForm.requires_job_description}
                        onChange={e => setCategoryForm({ ...categoryForm, requires_job_description: e.target.checked })}
                      />
                      Requires Job Description
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={categoryForm.requires_company_name}
                        onChange={e => setCategoryForm({ ...categoryForm, requires_company_name: e.target.checked })}
                      />
                      Requires Company Name
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCategoryModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update' : 'Create'} Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="modal-overlay" onClick={() => setShowSubcategoryModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSubcategory ? 'Edit Subcategory' : 'New Subcategory'}</h2>
              <button className="modal-close" onClick={() => setShowSubcategoryModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubcategorySubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Icon</label>
                    <input
                      type="text"
                      value={subcategoryForm.icon}
                      onChange={e => setSubcategoryForm({ ...subcategoryForm, icon: e.target.value })}
                      className="form-input icon-input"
                      placeholder="📌"
                    />
                  </div>
                  <div className="form-group flex-grow">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={subcategoryForm.name}
                      onChange={e => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                      className="form-input"
                      required
                      placeholder="e.g., Behavioral Questions"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    value={subcategoryForm.slug}
                    onChange={e => setSubcategoryForm({ ...subcategoryForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="form-input"
                    required
                    placeholder="e.g., behavioral"
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={subcategoryForm.description}
                    onChange={e => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
                    className="form-input"
                    rows={3}
                    placeholder="Brief description"
                  />
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={subcategoryForm.display_order}
                    onChange={e => setSubcategoryForm({ ...subcategoryForm, display_order: parseInt(e.target.value) || 0 })}
                    className="form-input"
                    min={0}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSubcategoryModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSubcategory ? 'Update' : 'Create'} Subcategory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;